/** @file Samples every timeline of an animation at a time into a sparse Sample. */
import { invariant } from '@pose2d/std/invariant';
import type { Sample } from '../contracts/sample';
import type { SkeletonData, TimelineData } from '../skeletonData';
import { timelineHandlers } from './timelineHandlers';

/** Any handler seen through the common signature (method parameters are bivariant, so every entry fits). */
interface AnyTimelineHandler {
  sample(timeline: TimelineData, time: number, lastTime: number, out: Sample, data: SkeletonData): void;
}

const HANDLERS = timelineHandlers();

/** Unmasks every channel of out (values zeroed, discrete channels "not keyed", no events). */
function clearSample(out: Sample): void {
  out.values.fill(0);
  out.mask.fill(0);
  out.inherit.fill(-1);
  out.attachments.fill(-2);
  out.drawOrderKeyed = false;
  out.deformKeyed.fill(0);
  out.eventCount = 0;
}

/** Clears out and samples animation anim at time seconds (events from the half-open interval [lastTime, time)). */
export function sampleAnimation(out: Sample, data: SkeletonData, anim: number, time: number, lastTime: number): void {
  clearSample(out);
  const animation = data.animations[anim];
  invariant(animation, 'sampleAnimation: unknown animation index');
  const timelines = animation.timelines;
  for (let i = 0; i < timelines.length; i++) {
    const tl = timelines[i];
    if (tl === undefined) continue;
    const handler: AnyTimelineHandler = HANDLERS[tl.k];
    handler.sample(tl, time, lastTime, out, data);
  }
}
